import moment from "moment";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Autocomplete, AutocompleteAPI, DateTimePicker, FormField } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import useDataCommon from "hooks/useDataCommon.hook";
import UserService from "services/identity/user.service";
import ReportService from "services/medkit/report.service";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { BooleanInt, ReportStatus, Status } from "constants/enum";
import { ICodename, IDataDetailAssign } from "commons/interfaces";
import { setDataAlert, showLoading } from "store/slice/common.slice";



const userService = new UserService();
const reportService = new ReportService();

const ReportAssignScreen = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const { getDataUsers } = useDataCommon();

    const workingOrganization = useSelector((state: RootState) => state.user.workingOrganization);
    const dataMapCodenames = useSelector((state: RootState) => state.common.codenames);
    const listPathName = useSelector((state: RootState) => state.titleRoute.listPathName);

    const [errorAssign, setErrorAssign] = useState<any>({});
    const [dataAssign, setDataAssign] = useState<IDataDetailAssign>({});
    const [itemInchargedStaff, setItemInchargedStaff] = useState<ICodename | undefined>(undefined);
    const [itemCoordinatedStaff, setItemCoordinatedStaff] = useState<ICodename | undefined>(undefined);

    const reportId: string = searchParams.get("id");
    useEffect(() => { reportId && getDetailReport(reportId); }, [reportId]);

    const requestGetApiUser = useMemo(() => ({
        listStatus: [Status.Active],
        organizationId: workingOrganization || "0",
    }), [workingOrganization]);

    const getDetailReport = async (id: string) => {
        try {
            dispatch(showLoading(true));

            const result = await reportService.detail(id);

            let userIds: string[] = [];
            if (!Helpers.isNullOrEmpty(result?.inchargedStaff)) {
                userIds.push(result?.inchargedStaff);
                setItemInchargedStaff({ code: result?.inchargedStaff, name: result?.inchargedStaffName });
            };
            if (!Helpers.isNullOrEmpty(result?.coordinatedStaff)) {
                userIds.push(result?.coordinatedStaff);
                setItemCoordinatedStaff({ code: result?.coordinatedStaff, name: result?.coordinatedStaffName });
            };
            userIds = Helpers.uniqueArray(userIds);
            if (userIds.length > 0) {
                const resultUser = await userService.getPaged({
                    nonHierarchical: BooleanInt.False,
                    selectedIds: userIds,
                    organizationId: workingOrganization,
                });
                for (const element of [...resultUser?.selectedItems || []]) {
                    let fullName = element.fullName;
                    if (element.organizationUserProfile?.lastName || element.organizationUserProfile?.firstName) {
                        fullName = `${element.organizationUserProfile?.lastName || ""} ${element.organizationUserProfile?.firstName || ""}`.trim();
                    };
                    if (element.id === result?.inchargedStaff) {
                        setItemInchargedStaff({ code: element.id, name: fullName });
                    };
                    if (element.id === result?.coordinatedStaff) {
                        setItemCoordinatedStaff({ code: element.id, name: fullName });
                    }
                };
            };

            setErrorAssign({});
            setDataAssign({
                duedate: result?.duedate || undefined,
                reportId: result?.id,
                reportNo: result?.reportNo,
                reportStatus: result?.reportStatus,
                implementPlan: result?.implementPlan,
                scheduleFinishDate: result?.scheduleFinishDate || undefined,
                inchargedStaff: result?.inchargedStaff,
                inchargedStaffTitle: result?.inchargedStaffTitle,
                coordinatedStaff: result?.coordinatedStaff,
                coordinatedStaffTitle: result?.coordinatedStaffTitle,
            });

        } catch (error) {

        } finally {
            dispatch(showLoading(false));
        }
    }

    const handleSubmitPress = async () => {
        try {
            dispatch(showLoading(true));

            const status = dataAssign?.reportStatus || ReportStatus.new;

            await reportService.assignReport({
                reportStatus: status,
                id: dataAssign?.reportId as string,
                duedate: dataAssign?.duedate,
                implementPlan: dataAssign?.implementPlan,
                scheduleFinishDate: dataAssign?.scheduleFinishDate,
                coordinatedStaff: dataAssign?.coordinatedStaff,
                coordinatedStaffTitle: dataAssign?.coordinatedStaffTitle,
                inchargedStaff: dataAssign?.inchargedStaff,
                inchargedStaffTitle: dataAssign?.inchargedStaffTitle,
            });

            dispatch(setDataAlert({ message: Strings.ASSIGN.SUBMIT_SUCCESS, type: "success" }));

            handleGoBack();
        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoading(false));
        }
    };

    const handleChangeValue = (key: string, value: any) => {
        setDataAssign(prev => ({ ...prev, [key]: value }));
        setErrorAssign((prev: any) => ({ ...prev, [key]: undefined }));
    };

    const handleGoBack = () => {
        const itemP = listPathName.find((el) => el.pathName === Screens.MONITORING);
        navigate(Screens.MONITORING + (itemP ? itemP.query : ""));
    };

    return (
        <DashboardLayout
            isPermission
            requiredOrganization
            title={Strings.ASSIGN.ASSIGN}
            route={[
                { title: Strings.MONITORING.TITLE, route: Screens.MONITORING },
                { title: Strings.ASSIGN.ASSIGN, route: "" },
            ]}
            listActions={[
                {
                    color: "secondary",
                    onPress: handleGoBack,
                    title: Strings.COMMON.BACK,
                },
                {
                    color: "dark",
                    onPress: handleSubmitPress,
                    title: Strings.COMMON.SAVE,
                },
            ]}
        >
            <Card sx={{ padding: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormField
                            disabled
                            variant={"outlined"}
                            label={Strings.REPORT.REPORT_CODE}
                            value={dataAssign?.reportNo || ""}
                            onChangeValue={(val) => { }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            variant={"outlined"}
                            label={Strings.ASSIGN.REPORT_STATUS}
                            defaultValue={dataAssign?.reportStatus}
                            errorMessage={errorAssign?.reportStatus}
                            data={[...dataMapCodenames?.reportStatus || []].filter(codename => codename.code === ReportStatus.processing || codename.code === ReportStatus.processed)}
                            placeholder={Strings.ASSIGN.SELECT_REPORT_STATUS}
                            onChange={(val) => {
                                handleChangeValue("reportStatus", val);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <AutocompleteAPI
                            variant={"outlined"}
                            getApi={(requestGetApi) => getDataUsers(requestGetApi, true)}
                            requestGetApi={requestGetApiUser}
                            label={Strings.ASSIGN.INCHARGED_STAFF}
                            defaultValue={dataAssign?.inchargedStaff}
                            errorMessage={errorAssign?.inchargedStaff}
                            placeholder={Strings.ASSIGN.SELECT_INCHARGED_STAFF}
                            dataSelected={itemInchargedStaff ? [itemInchargedStaff] : []}
                            onChange={(val) => { }}
                            onChangeItem={(val) => {
                                setItemInchargedStaff(val);
                                handleChangeValue("inchargedStaff", val?.code);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormField
                            variant={"outlined"}
                            label={Strings.ASSIGN.INCHARGED_STAFF_TITLE}
                            defaultValue={dataAssign?.inchargedStaffTitle}
                            errorMessage={errorAssign?.inchargedStaffTitle}
                            placeholder={Strings.ASSIGN.ENTER_INCHARGED_STAFF_TITLE}
                            onBlur={(val) => {
                                handleChangeValue("inchargedStaffTitle", val);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <AutocompleteAPI
                            variant={"outlined"}
                            getApi={(requestGetApi) => getDataUsers(requestGetApi, true)}
                            requestGetApi={requestGetApiUser}
                            label={Strings.ASSIGN.COORDINATED_STAFF}
                            defaultValue={dataAssign?.coordinatedStaff}
                            errorMessage={errorAssign?.coordinatedStaff}
                            placeholder={Strings.ASSIGN.SELECT_COORDINATED_STAFF}
                            dataSelected={itemCoordinatedStaff ? [itemCoordinatedStaff] : []}
                            onChange={(val) => { }}
                            onChangeItem={(val) => {
                                setItemCoordinatedStaff(val);
                                handleChangeValue("coordinatedStaff", val?.code);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormField
                            variant={"outlined"}
                            label={Strings.ASSIGN.COORDINATED_STAFF_TITLE}
                            defaultValue={dataAssign?.coordinatedStaffTitle}
                            errorMessage={errorAssign?.coordinatedStaffTitle}
                            placeholder={Strings.ASSIGN.ENTER_COORDINATED_STAFF_TITLE}
                            onBlur={(val) => {
                                handleChangeValue("coordinatedStaffTitle", val);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <FormField
                            multiline
                            minRows={5}
                            variant={"outlined"}
                            label={Strings.ASSIGN.IMPLEMENT_PLAN}
                            defaultValue={dataAssign?.implementPlan}
                            errorMessage={errorAssign?.implementPlan}
                            placeholder={Strings.ASSIGN.ENTER_IMPLEMENT_PLAN}
                            onBlur={(val) => {
                                handleChangeValue("implementPlan", val);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateTimePicker
                            variant={"outlined"}
                            label={Strings.ASSIGN.DUE_DATE}
                            errorMessage={errorAssign?.duedate}
                            key={"duedate_" + dataAssign?.duedate}
                            value={Helpers.toUnix(dataAssign?.duedate)}
                            onChangeValue={(val) => {
                                const newValue = val ? moment(val).unix() : undefined;
                                handleChangeValue("duedate", newValue);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateTimePicker
                            variant={"outlined"}
                            label={Strings.ASSIGN.SCHEDULE_FINISH_DATE}
                            errorMessage={errorAssign?.scheduleFinishDate}
                            key={"schedule_" + dataAssign?.scheduleFinishDate}
                            value={Helpers.toUnix(dataAssign?.scheduleFinishDate)}
                            onChangeValue={(val) => {
                                const newValue = val ? moment(val).unix() : undefined;
                                handleChangeValue("scheduleFinishDate", newValue);
                            }}
                        />
                    </Grid>
                </Grid>
            </Card>
        </DashboardLayout>
    );
};

export default ReportAssignScreen;
